/**
 * @module  public/js/core/auth/AuthResilience.js
 * @summary Adds retry and circuit-breaker resilience to Firebase sign-in flows.
 *
 * @description
 * Provides a small stateful controller that wraps `signInWithEmailAndPassword`
 * with exponential backoff retries and a rolling circuit breaker. Consumers can
 * inject custom timers, telemetry collectors, or loggers to tailor behaviour in
 * different environments. Defaults are tuned for flaky mobile networks yet can
 * be relaxed/tightened through dependency-injected options.
 */

const DEFAULT_NON_RETRYABLE_CODES = [
  'auth/invalid-email',
  'auth/user-not-found',
  'auth/wrong-password',
  'auth/too-many-requests',
  'auth/invalid-credential',
];

const DEFAULT_CONFIG = {
  maxAttempts: 3,
  initialDelayMs: 250,
  maxDelayMs: 4_000,
  backoffFactor: 2,
  jitterRatio: 0.2,
  breakerThreshold: 5,
  breakerCooldownMs: 15_000,
  failureWindowMs: 60_000,
  nonRetryableErrorCodes: DEFAULT_NON_RETRYABLE_CODES,
};

/**
 * Normalises arbitrary numeric input into a bounded positive integer.
 *
 * @param {unknown} value raw input value.
 * @param {number} fallback fallback when the input is not a safe number.
 * @param {{ min?: number, max?: number }} [bounds] optional min/max bounds.
 * @returns {number} sanitised integer within the provided bounds.
 */
function toBoundedInteger(value, fallback, bounds = {}) {
  const parsed = Number.parseInt(String(value ?? ''), 10);
  const min = typeof bounds.min === 'number' ? bounds.min : 0;
  const max =
    typeof bounds.max === 'number' ? bounds.max : Number.POSITIVE_INFINITY;

  if (!Number.isFinite(parsed)) {
    return fallback;
  }

  return Math.min(Math.max(parsed, min), max);
}

/**
 * Normalises arbitrary numeric input into a bounded positive float.
 *
 * @param {unknown} value raw input value.
 * @param {number} fallback fallback when the input is not a safe number.
 * @param {{ min?: number, max?: number }} [bounds] optional min/max bounds.
 * @returns {number} sanitised float within the provided bounds.
 */
function toBoundedFloat(value, fallback, bounds = {}) {
  const parsed = Number.parseFloat(String(value ?? ''));
  const min = typeof bounds.min === 'number' ? bounds.min : 0;
  const max =
    typeof bounds.max === 'number' ? bounds.max : Number.POSITIVE_INFINITY;

  if (!Number.isFinite(parsed)) {
    return fallback;
  }

  return Math.min(Math.max(parsed, min), max);
}

/**
 * Provides a default `Date.now()` wrapper for dependency injection.
 *
 * @returns {number} milliseconds elapsed since epoch.
 */
function defaultNow() {
  return Date.now();
}

/**
 * Provides a default wait implementation backed by `setTimeout`.
 *
 * @param {number} ms number of milliseconds to wait.
 * @returns {Promise<void>} promise resolving after the delay.
 */
function defaultWait(ms) {
  const safeDelay = Math.max(0, Number.isFinite(ms) ? Number(ms) : 0);

  if (safeDelay === 0) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    setTimeout(resolve, safeDelay);
  });
}

/**
 * No-op logger fallback used when no logger is supplied.
 */
const nullLogger = {
  info: () => {},
  warn: () => {},
  error: () => {},
};

/**
 * @typedef {Object} AuthResilienceConfig
 * @property {number} maxAttempts maximum attempts (initial + retries).
 * @property {number} initialDelayMs base backoff delay for the first retry.
 * @property {number} maxDelayMs ceiling applied to computed backoff delays.
 * @property {number} backoffFactor multiplier applied between retries.
 * @property {number} jitterRatio jitter ratio applied to each delay (0-1).
 * @property {number} breakerThreshold failures within the window before tripping the breaker.
 * @property {number} breakerCooldownMs cooldown before the breaker closes again.
 * @property {number} failureWindowMs lookback window for breaker failure counts.
 * @property {Array<string>|Set<string>} nonRetryableErrorCodes whitelist of error codes that should not be retried.
 */

/**
 * @typedef {Object} AuthResilienceDependencies
 * @property {import('firebase/auth').Auth} auth Firebase auth instance.
 * @property {(auth: import('firebase/auth').Auth, email: string, password: string) => Promise<import('firebase/auth').UserCredential>} signInWithEmailAndPassword
 *          Firebase sign-in function.
 * @property {{ startAttempt?: (context: Record<string, unknown>) => void, endAttempt?: (payload: { result: string, error?: unknown }) => void }} [telemetry]
 *          optional telemetry bridge compatible with `AuthTelemetry`.
 * @property {{ info?: (...args: unknown[]) => void, warn?: (...args: unknown[]) => void, error?: (...args: unknown[]) => void }} [logger]
 *          optional logger receiving resilience lifecycle events.
 * @property {() => number} [now] monotonic clock override.
 * @property {(ms: number) => Promise<void>} [wait] async delay implementation.
 * @property {() => number} [random] RNG returning 0-1 floats for jitter calculations.
 * @property {Partial<AuthResilienceConfig>} [config] configuration overrides.
 */

/**
 * Stateful controller that wraps Firebase sign-in with retries and a circuit breaker.
 */
export class AuthResilienceController {
  /**
   * @param {AuthResilienceDependencies} options configuration & dependency overrides.
   */
  constructor(options = {}) {
    const {
      auth,
      signInWithEmailAndPassword,
      telemetry = null,
      logger = null,
      now,
      wait,
      random,
      config = {},
    } = options;

    if (!auth) {
      throw new Error('AuthResilience requires a Firebase auth instance.');
    }

    if (typeof signInWithEmailAndPassword !== 'function') {
      throw new TypeError(
        'AuthResilience requires the signInWithEmailAndPassword function.'
      );
    }

    const mergedConfig = {
      ...DEFAULT_CONFIG,
      ...(config || {}),
    };

    this._config = {
      maxAttempts: toBoundedInteger(
        mergedConfig.maxAttempts,
        DEFAULT_CONFIG.maxAttempts,
        {
          min: 1,
          max: 10,
        }
      ),
      initialDelayMs: toBoundedInteger(
        mergedConfig.initialDelayMs,
        DEFAULT_CONFIG.initialDelayMs,
        { min: 0, max: 120_000 }
      ),
      maxDelayMs: toBoundedInteger(
        mergedConfig.maxDelayMs,
        DEFAULT_CONFIG.maxDelayMs,
        {
          min: 100,
          max: 120_000,
        }
      ),
      backoffFactor: toBoundedFloat(
        mergedConfig.backoffFactor,
        DEFAULT_CONFIG.backoffFactor,
        {
          min: 1,
          max: 10,
        }
      ),
      jitterRatio: toBoundedFloat(
        mergedConfig.jitterRatio,
        DEFAULT_CONFIG.jitterRatio,
        {
          min: 0,
          max: 0.5,
        }
      ),
      breakerThreshold: toBoundedInteger(
        mergedConfig.breakerThreshold,
        DEFAULT_CONFIG.breakerThreshold,
        { min: 1, max: 20 }
      ),
      breakerCooldownMs: toBoundedInteger(
        mergedConfig.breakerCooldownMs,
        DEFAULT_CONFIG.breakerCooldownMs,
        { min: 1_000, max: 300_000 }
      ),
      failureWindowMs: toBoundedInteger(
        mergedConfig.failureWindowMs,
        DEFAULT_CONFIG.failureWindowMs,
        { min: 1_000, max: 300_000 }
      ),
    };

    const nonRetryable = mergedConfig.nonRetryableErrorCodes;
    const codesArray = Array.isArray(nonRetryable)
      ? nonRetryable
      : nonRetryable instanceof Set
        ? Array.from(nonRetryable)
        : DEFAULT_NON_RETRYABLE_CODES;

    this._nonRetryableCodes = new Set(codesArray);

    /** @private */
    this._auth = auth;
    /** @private */
    this._signInWithEmailAndPassword = signInWithEmailAndPassword;
    /** @private */
    this._telemetry = telemetry;
    /** @private */
    this._logger = logger ?? console ?? nullLogger;
    /** @private */
    this._now = typeof now === 'function' ? now : defaultNow;
    /** @private */
    this._wait = typeof wait === 'function' ? wait : defaultWait;
    /** @private */
    this._random = typeof random === 'function' ? random : Math.random;
    /** @private */
    this._failureTimestamps = [];
    /** @private */
    this._breakerOpenUntil = 0;
  }

  /**
   * Returns whether the circuit breaker is currently open.
   *
   * @returns {boolean} `true` when the breaker is enforcing a cooldown.
   */
  isCircuitOpen() {
    return this._now() < this._breakerOpenUntil;
  }

  /**
   * Returns a snapshot of the breaker's internal state.
   *
   * @returns {{ isOpen: boolean, openUntil: number, recentFailures: number }}
   *          structural clone for diagnostics/testing.
   */
  getState() {
    return {
      isOpen: this.isCircuitOpen(),
      openUntil: this._breakerOpenUntil,
      recentFailures: this._failureTimestamps.length,
    };
  }

  /**
   * Forces the breaker closed and clears historical failures.
   */
  resetBreaker() {
    this._breakerOpenUntil = 0;
    this._failureTimestamps = [];
  }

  /**
   * Attempts a sign-in while applying retries and circuit-breaker safety.
   *
   * @param {string} email user email address.
   * @param {string} password user password.
   * @param {Record<string, unknown>} [context] optional telemetry context.
   * @returns {Promise<import('firebase/auth').UserCredential>} Firebase credential.
   */
  async signIn(email, password, context = {}) {
    if (this.isCircuitOpen()) {
      this._emitTelemetryStart({ ...context, retryCount: 0 });
      const circuitError = this._createCircuitError();
      this._emitTelemetryEnd({ result: 'CIRCUIT_OPEN', error: circuitError });
      this._logger.warn?.('AuthResilience: circuit breaker open', {
        openUntil: this._breakerOpenUntil,
      });
      throw circuitError;
    }

    const { maxAttempts } = this._config;
    let lastError = null;

    for (let attemptIndex = 0; attemptIndex < maxAttempts; attemptIndex += 1) {
      this._emitTelemetryStart({ ...context, retryCount: attemptIndex });

      try {
        const credential = await this._signInWithEmailAndPassword(
          this._auth,
          email,
          password
        );
        this._recordSuccess();
        this._emitTelemetryEnd({ result: 'SUCCESS', error: null });
        return credential;
      } catch (error) {
        lastError = error;
        this._recordFailure();

        const shouldRetry = this._shouldRetry(error, attemptIndex);
        this._emitTelemetryEnd({
          result: shouldRetry ? 'RETRYING' : 'FAILURE',
          error,
        });

        if (!shouldRetry) {
          throw error;
        }

        if (this.isCircuitOpen()) {
          const circuitError = this._createCircuitError(error);
          this._logger.warn?.('AuthResilience: breaker opened mid-flight', {
            retryCount: attemptIndex,
            openUntil: this._breakerOpenUntil,
          });
          throw circuitError;
        }

        const delay = this._nextDelay(attemptIndex + 1);
        this._logger.warn?.('AuthResilience: retrying sign-in', {
          retryCount: attemptIndex + 1,
          delay,
          code: error && typeof error === 'object' ? error.code : undefined,
        });
        await this._wait(delay);
      }
    }

    throw (
      lastError ?? new Error('AuthResilience exhausted retries without error.')
    );
  }

  /** @private */
  _nextDelay(retryIndex) {
    const { initialDelayMs, backoffFactor, maxDelayMs, jitterRatio } =
      this._config;
    const exponent = retryIndex - 1;
    const baseDelay = initialDelayMs * backoffFactor ** Math.max(0, exponent);
    const boundedDelay = Math.min(baseDelay, maxDelayMs);

    if (jitterRatio <= 0) {
      return Math.round(boundedDelay);
    }

    const jitter = boundedDelay * jitterRatio * (this._random() * 2 - 1);
    const finalDelay = Math.max(0, Math.min(maxDelayMs, boundedDelay + jitter));
    return Math.round(finalDelay);
  }

  /** @private */
  _shouldRetry(error, attemptIndex) {
    if (attemptIndex >= this._config.maxAttempts - 1) {
      return false;
    }

    if (!error || typeof error !== 'object') {
      return true;
    }

    const code = /** @type {{ code?: unknown }} */ (error).code;

    if (typeof code === 'string' && this._nonRetryableCodes.has(code)) {
      return false;
    }

    const status = /** @type {{ status?: unknown }} */ (error).status;

    if (typeof status === 'number' && status >= 400 && status < 500) {
      return false;
    }

    if (typeof status === 'string') {
      const parsed = Number.parseInt(status, 10);
      if (Number.isFinite(parsed) && parsed >= 400 && parsed < 500) {
        return false;
      }
    }

    return true;
  }

  /** @private */
  _recordFailure() {
    const now = this._now();
    this._failureTimestamps.push(now);
    const cutoff = now - this._config.failureWindowMs;
    this._failureTimestamps = this._failureTimestamps.filter(
      (ts) => ts >= cutoff
    );

    if (this._failureTimestamps.length >= this._config.breakerThreshold) {
      this._openCircuit(now);
    }
  }

  /** @private */
  _recordSuccess() {
    this.resetBreaker();
  }

  /** @private */
  _openCircuit(now) {
    const openUntil =
      (typeof now === 'number' ? now : this._now()) +
      this._config.breakerCooldownMs;
    this._breakerOpenUntil = openUntil;
    this._logger.warn?.('AuthResilience: circuit breaker opened', {
      openUntil,
      failureCount: this._failureTimestamps.length,
    });
  }

  /** @private */
  _emitTelemetryStart(context) {
    if (
      !this._telemetry ||
      typeof this._telemetry.startAttempt !== 'function'
    ) {
      return;
    }

    try {
      this._telemetry.startAttempt(context);
    } catch (err) {
      this._logger.warn?.('AuthResilience telemetry start failed', err);
    }
  }

  /** @private */
  _emitTelemetryEnd(payload) {
    if (!this._telemetry || typeof this._telemetry.endAttempt !== 'function') {
      return;
    }

    try {
      this._telemetry.endAttempt(payload);
    } catch (err) {
      this._logger.warn?.('AuthResilience telemetry end failed', err);
    }
  }

  /** @private */
  _createCircuitError(cause) {
    const error = new Error(
      'Authentication temporarily disabled due to network instability.'
    );
    /** @type {{ code?: string, cause?: unknown }} */ (error).code =
      'auth/resilience-circuit-open';
    if (typeof cause !== 'undefined') {
      /** @type {{ cause?: unknown }} */ (error).cause = cause;
    }
    return error;
  }
}

/**
 * Factory helper mirroring the runtime API for ease of injection.
 *
 * @param {AuthResilienceDependencies} options dependency/configuration overrides.
 * @returns {AuthResilienceController} configured resilience controller.
 */
export function AuthResilience(options = {}) {
  return new AuthResilienceController(options);
}

export default AuthResilience;
